import { useState, useRef, useEffect } from 'react';
import { X, ScrollText } from 'lucide-react';

/**
 * Terms & Conditions modal with scroll-to-accept.
 */
export default function TermsModal({ open, onClose, onAccept, role = 'investor' }) {
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (open) setScrolledToEnd(false);
  }, [open]);

  const handleScroll = () => {
    const el = contentRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setScrolledToEnd(true);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="terms-modal">
      <div className="bg-[#111] border border-white/10 rounded-xl max-w-lg w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-white">
            <ScrollText className="w-5 h-5 text-[#4ADE80]" />
            <span className="font-medium">Terms & Conditions</span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="terms-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div ref={contentRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-6 text-sm text-gray-300 space-y-4 leading-relaxed">
          <h3 className="text-white text-base font-medium">AltSlice Platform — Terms of Service (v1.2)</h3>
          <p className="text-gray-500 text-xs">Last updated: February 2026</p>

          <section>
            <h4 className="text-white font-medium mb-1">1. Acceptance of Terms</h4>
            <p>By creating an account on AltSlice, you agree to these Terms of Service and our Privacy Policy. If you do not agree, you may not use the platform.</p>
          </section>

          <section>
            <h4 className="text-white font-medium mb-1">2. Eligibility</h4>
            <p>You must be at least 18 years old and a resident of India. {role === 'broker' ? 'Brokers must hold a valid RERA registration.' : 'Investors must complete KYC verification before making investments.'}</p>
          </section>

          <section>
            <h4 className="text-white font-medium mb-1">3. Account Security</h4>
            <p>You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account. Notify us immediately of any unauthorized use.</p>
          </section>

          <section>
            <h4 className="text-white font-medium mb-1">4. Investment Risks</h4>
            <p>Real estate investments carry inherent risks including market fluctuation, illiquidity, and potential loss of capital. Past performance does not guarantee future results. AltSlice does not provide financial advice.</p>
          </section>

          <section>
            <h4 className="text-white font-medium mb-1">5. KYC & Compliance</h4>
            <p>You agree to provide accurate personal information and complete KYC verification as required by Indian regulations. Failure to do so may result in account restrictions.</p>
          </section>

          <section>
            <h4 className="text-white font-medium mb-1">6. Data Privacy</h4>
            <p>We collect and process personal data in accordance with our Privacy Policy. Your data is encrypted and stored securely. We do not sell personal information to third parties.</p>
          </section>

          <section>
            <h4 className="text-white font-medium mb-1">7. Communications</h4>
            <p>By registering, you consent to receiving transactional emails, OTP messages, and investment-related notifications via email, SMS, and WhatsApp.</p>
          </section>

          <section>
            <h4 className="text-white font-medium mb-1">8. Intellectual Property</h4>
            <p>All content, trademarks, and intellectual property on the AltSlice platform are owned by AltSlice Technologies Pvt. Ltd. Unauthorized reproduction is prohibited.</p>
          </section>

          <section>
            <h4 className="text-white font-medium mb-1">9. Limitation of Liability</h4>
            <p>AltSlice shall not be liable for any indirect, incidental, or consequential damages arising from use of the platform. Our total liability is limited to the fees paid by you.</p>
          </section>

          <section>
            <h4 className="text-white font-medium mb-1">10. Governing Law</h4>
            <p>These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Bengaluru, Karnataka.</p>
          </section>

          <div className="pt-4 text-xs text-gray-500 border-t border-white/10">
            Version: {role}_v1.2 | Effective: February 2026
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {scrolledToEnd ? 'You have read the terms' : 'Please scroll to the end to accept'}
          </p>
          <button
            onClick={onAccept}
            disabled={!scrolledToEnd}
            className="px-6 py-2 bg-[#4ADE80] text-black font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#4ADE80]/90 transition-colors"
            data-testid="terms-accept-btn"
          >
            I Accept
          </button>
        </div>
      </div>
    </div>
  );
}
